import {
  CloseCircleFilled,
  CloseOutlined,
  EditFilled,
  SaveFilled,
} from '@ant-design/icons'
import { Button, Drawer, Form, Input, Space, message, Grid } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as PersonLogoFilled } from '../../assets/Icons_User Fill.svg'
import { ReactComponent as LockLogoFilled } from '../../assets/lock.svg'
import ProfilePicture from '../../components/ProfilePicture/ProfilePicture'
import QrCode from '../../components/QrCode/QrCode'
import { displayMessage } from '../../helpers/Message'
import {
  changePassword,
  errorAuth,
  selectAccountUk,
  selectErrorInAuth,
  selectSuccessInAuth,
  setUserDataForForm,
  successAuth,
  updateUser,
} from '../../state/auth/authReducer'
import {
  AccountScreenInnerContainer,
  AccountScreenInnerContainerTitle,
  AccountScreenInnerContainerTitleContainer,
  AccountScreenInnerContainerTitleLogo,
  AccountTabs,
} from './AcountScreenStyle'

type AccountScreenProps = {
  open: boolean
  onClose: () => void
}

function AccountScreen({ open, onClose }: AccountScreenProps) {
  const dispatch = useDispatch()
  const authError = useSelector(selectErrorInAuth)
  const authSuccess = useSelector(selectSuccessInAuth)

  const [messageApi, contextHolder] = message.useMessage()

  const screens = Grid.useBreakpoint();
  const drawerWidth = screens.lg ? '50vw' : '100%'; 

  useEffect(() => {
    if (authError) {
      displayMessage(authError, 'error', messageApi)
      dispatch(errorAuth(null))
    }
  }, [authError])

  useEffect(() => {
    if (authSuccess) {
      displayMessage(authSuccess, 'success', messageApi)
      dispatch(successAuth(null))
    }
  }, [authSuccess])

  return (
    <Drawer
      title={'My Account'}
      width={drawerWidth}
      style={{
        overflow: 'hidden',
      }}
      zIndex={99999999}
      className='myAccountDrawer'
      onClose={onClose}
      closeIcon={false}
      open={open}
      placement='left'
      extra={
        <Space>
          <Button shape='circle' type='text' onClick={onClose}>
            <CloseOutlined />
          </Button>
        </Space>
      }
    >
      {contextHolder}
      <AccountTabs
        type='card'
        items={[
          {
            label: `User Profile`,
            key: '1',
            children: <UserProfileTab />,
          },
          {
            label: `Change Password`,
            key: '2',
            children: <ChangePasswordTab />,
          },
          {
            label: `QR Code`,
            key: '3',
            children: <QrTab />,
          },
        ]}
      />
    </Drawer>
  )
}

export default AccountScreen

function UserProfileTab() {
  const [updateUserState, setUpdateUserState] = useState(false)
  const [userForm] = useForm()
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const authSuccess = useSelector(selectSuccessInAuth)
  const authError = useSelector(selectErrorInAuth)
  useEffect(() => {
    if (authSuccess) {
      setLoading(false)
      setUpdateUserState(false)
    }
    if (authError) {
      setLoading(false)
    }
  }, [authSuccess])
  useEffect(() => {
    dispatch(setUserDataForForm(userForm))
  }, [])

  const saveUserClick = () => {
    setLoading(true)
    userForm.submit()
  }

  const onUserFormFinish = (values: any) => {
    dispatch(updateUser(values))
  }

  const onFormFinishFailed = () => {
    setLoading(false)
  }

  return (
    <AccountScreenInnerContainer>
      <AccountScreenInnerContainerTitleContainer>
        <AccountScreenInnerContainerTitle>
          <AccountScreenInnerContainerTitleLogo component={PersonLogoFilled} />
          Profile
        </AccountScreenInnerContainerTitle>
        <div className='updateUserControlContainer'>
          {!updateUserState ? (
            <Button
              onClick={() => setUpdateUserState(true)}
              size='small'
              className='iconButton'
            >
              {' '}
              <EditFilled className='anticon' /> Update
            </Button>
          ) : (
            <>
              <Button onClick={() => setUpdateUserState(false)} size='small'>
                <CloseCircleFilled className='anticon' /> Cancel
              </Button>
              <Button
                loading={loading}
                onClick={saveUserClick}
                icon={<SaveFilled className='anticon' />}
                size='small'
              >
                {' '}
                Save
              </Button>
            </>
          )}
        </div>
      </AccountScreenInnerContainerTitleContainer>
      <ProfilePicture updateState={updateUserState} />
      <Form
        onFinish={onUserFormFinish}
        form={userForm}
        onFinishFailed={onFormFinishFailed}
        disabled={!updateUserState}
        layout='vertical'
        className='disabledViewForm accountForm'
        requiredMark={updateUserState}
      >
        <Form.Item
          label='Title'
          name='title'
          rules={[
            { required: true, message: 'Please enter title' },
            { pattern: /^[A-Za-z.]*$/, message: 'Enter a Valid Title' },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label='First Name'
          name='firstName'
          rules={[
            { required: true, message: 'Please enter first name' },
            {
              pattern: /^[a-zA-Z0-9.'\- ]+$/,
              message: 'Enter a valid first name',
            },
          ]}
        >
          <Input name='firstName' />
        </Form.Item>
        <Form.Item
          label='Last Name'
          name='lastName'
          rules={[
            { required: true, message: 'Please enter last name' },
            {
              pattern: /^[a-zA-Z0-9.'\- ]+$/,
              message: 'Enter a valid last name',
            },
          ]}
        >
          <Input name='lastName' />
        </Form.Item>
        <Form.Item
          name='jobTitle'
          label='Job Title'
          rules={[{ required: true, message: 'Please enter Job Title' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name='phonePrimary'
          label='Primary Phone'
          rules={[
            { required: true, message: 'Please enter primary phone number' },
            {
              pattern: /^\+(\d{1,4})\d{7,10}$/,
              message: 'Please enter valid phone number',
            },
          ]}
          extra='Use +61 format. Example: +61412345678'
        >
          <Input />
        </Form.Item>
        <Form.Item
          name='phoneSecondary'
          label='Secondary Phone'
          rules={[
            {
              pattern: /^\+(\d{1,4})\d{7,10}$/,
              message: 'Please enter valid phone number',
            },
          ]}
          extra='Use +61 format. Example: +61412345678'
        >
          <Input />
        </Form.Item>
      </Form>
    </AccountScreenInnerContainer>
  )
}

function ChangePasswordTab() {
  const [changePasswordState, setChangePasswordState] = useState(false)
  const [changePasswordForm] = useForm()
  const authSuccess = useSelector(selectSuccessInAuth)
  const authError = useSelector(selectErrorInAuth)
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const [messageApi, contextHolder] = message.useMessage()

  const saveChangePasswordClick = () => {
    setLoading(true)
    changePasswordForm.submit()
  }

  useEffect(() => {
    if (authSuccess) {
      changePasswordForm.resetFields()
      setChangePasswordState(false)
      setLoading(false)
    }
    if (authError) {
      setLoading(false)
    }
  }, [authSuccess, authError])
  const onFormFinishFailed = () => {
    setLoading(false)
  }

  const onChangePasswordFormFinish = (values: any) => {
    let newPassword = values.newPassword
    let confirmNewPassword = values.confirmNewPassword
    if (newPassword !== confirmNewPassword) {
      displayMessage(
        'new password and confirm new password are not the same',
        'error',
        messageApi
      )
    } else {
      dispatch(
        changePassword({
          newPassword: values.newPassword,
          oldPassword: values.oldPassword,
        })
      )
    }
  }
  return (
    <AccountScreenInnerContainer>
      {contextHolder}
      <AccountScreenInnerContainerTitleContainer>
        <AccountScreenInnerContainerTitle>
          <AccountScreenInnerContainerTitleLogo component={LockLogoFilled} />
          Change Password
        </AccountScreenInnerContainerTitle>
        <div className='updateUserControlContainer'>
          {!changePasswordState ? (
            <Button
              onClick={() => setChangePasswordState(true)}
              size='small'
              className='iconButton'
            >
              {' '}
              <EditFilled className='anticon' /> Change
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setChangePasswordState(false)
                  changePasswordForm.resetFields()
                }}
                size='small'
              >
                <CloseCircleFilled className='anticon' /> Cancel
              </Button>
              <Button
                icon={<SaveFilled className='anticon' />}
                loading={loading}
                onClick={saveChangePasswordClick}
                size='small'
              >
                {' '}
                Save
              </Button>
            </>
          )}
        </div>
      </AccountScreenInnerContainerTitleContainer>
      <Form
        onFinish={onChangePasswordFormFinish}
        form={changePasswordForm}
        disabled={!changePasswordState}
        layout='vertical'
        onFinishFailed={onFormFinishFailed}
        className='disabledViewForm accountForm'
        requiredMark={changePasswordState}
      >
        <Form.Item
          label='Old Password'
          name='oldPassword'
          rules={[{ required: true, message: 'Please enter the old password' }]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          label='New Password'
          name='newPassword'
          rules={[{ required: true, message: 'Please enter the new password' }]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          label='Confirm New Password'
          name='confirmNewPassword'
          rules={[
            { required: true, message: 'Please enter the new password again' },
          ]}
        >
          <Input.Password />
        </Form.Item>
      </Form>
    </AccountScreenInnerContainer>
  )
}

function QrTab() {
  const accountUk = useSelector(selectAccountUk)
  const qrData = `{uk:'${accountUk.uk}'}`

  return (
    <AccountScreenInnerContainer>
      <AccountScreenInnerContainerTitleContainer>
        <AccountScreenInnerContainerTitle>
          <AccountScreenInnerContainerTitleLogo />
          QR CODE
        </AccountScreenInnerContainerTitle>
        <div></div>
      </AccountScreenInnerContainerTitleContainer>
      <QrCode value={qrData} />
    </AccountScreenInnerContainer>
  )
}
